import type { OrgRole, ProfileRow } from "./adminTypes";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { ORG_ROLE_LABELS, formatAdminProfileName, orgRolesForAddDropdown } from "./adminTypes";

export type AdminPendingApprovalsBlockProps = {
  pendingProfiles: ProfileRow[];
  loading: boolean;
  getRoleFor: (profileId: string) => OrgRole;
  setRoleByUser: React.Dispatch<React.SetStateAction<Record<string, OrgRole>>>;
  blockReasonByUser: Record<string, string>;
  setBlockReasonByUser: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  canDeleteUser: boolean;
  canAssignManufacturingOrgRole: boolean;
  onApproveAndAssign: (profileId: string) => void;
  onBlock: (profileId: string, name: string) => void;
  onDeleteUser: (profileId: string, name: string) => void;
};

export function AdminPendingApprovalsBlock({
  pendingProfiles,
  loading,
  getRoleFor,
  setRoleByUser,
  blockReasonByUser,
  setBlockReasonByUser,
  canDeleteUser,
  canAssignManufacturingOrgRole,
  onApproveAndAssign,
  onBlock,
  onDeleteUser,
}: AdminPendingApprovalsBlockProps) {
  if (pendingProfiles.length === 0) {
    return (
      <div {...{ [FIREFLY_TARGET_ATTR]: "admin-pending-approvals" }} className="text-sm text-gray-500 py-1">
        No pending users for this org.
      </div>
    );
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "admin-pending-approvals" }} className="space-y-3">
      {pendingProfiles.map((p) => (
        <div key={p.id} className="p-3 rounded-lg border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-medium">{formatAdminProfileName(p)}</div>
              <div className="text-sm text-gray-600">{p.email}</div>
              <div className="text-xs text-gray-500 mt-1">
                domain: {p.email_domain ?? "?"} • created: {p.created_at ?? "?"}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white"
                value={getRoleFor(p.id)}
                onChange={(e) => setRoleByUser((prev) => ({ ...prev, [p.id]: e.target.value as OrgRole }))}
              >
                {orgRolesForAddDropdown(canAssignManufacturingOrgRole).map((r) => (
                  <option key={r} value={r}>
                    {ORG_ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onApproveAndAssign(p.id)}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
                disabled={loading}
              >
                Approve + assign
              </button>
              <button
                type="button"
                onClick={() => onBlock(p.id, formatAdminProfileName(p))}
                className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-60"
                disabled={loading}
              >
                Block
              </button>
              {canDeleteUser && (
                <button
                  type="button"
                  onClick={() => onDeleteUser(p.id, formatAdminProfileName(p))}
                  className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm hover:bg-red-50 disabled:opacity-60"
                  disabled={loading}
                >
                  Delete User
                </button>
              )}
            </div>
          </div>
          <div className="mt-2">
            <input
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Block reason (optional)"
              value={blockReasonByUser[p.id] ?? ""}
              onChange={(e) => setBlockReasonByUser((prev) => ({ ...prev, [p.id]: e.target.value }))}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
